import { Field, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import { Error } from "@progress/kendo-react-labels";
import companyApi from "../../../api/company";
import productsApi from "../../../api/products";
import { useApi, useFetch } from "../../../hooks/useApi";
import { useUserContext } from "../../../hooks/useUserContext";
import { FormDropDownList, ListItem } from "../../form";
import { ICompany } from "../../admin/Company";
import { requiredValidator } from "../../../validators";
import { useEffect, useState } from "react";
import { BusinessType } from "../../../types/businessType";
import { RoleUtility } from "../../../utilities/rolePermissionUtilities";
import { Commodity, ICommodity } from "../Commodity";
import { IInstrument, Instrument } from "../Instrument";
import { LoadingIndicator } from "../../LoadingIndicator";
import { UserCompany } from "../../../hooks/useCompanies";

interface IStartDealEntryProps {
  formRenderProps: FormRenderProps;
  readOnly: boolean;
}

export const StartDealEntryQuote = ({ formRenderProps, readOnly }: IStartDealEntryProps) => {
  const { context: user } = useUserContext();

  const {
    data: companiesData,
    loading: companiesLoading,
    error: companiesError,
  } = useFetch(companyApi.getLineage);

  const {
    data: commoditiesData,
    loading: commoditiesLoading,
    error: commoditiesError,
  } = useFetch(productsApi.getCommodities);

  const getInstrumentsApi = useApi(productsApi.getInstruments);

  const product = formRenderProps.valueGetter("product");

  const [companies, setCompanies] = useState<ListItem[]>([]);
  const [companyId, setCompanyId] = useState(product?.company?.id ?? 0);
  const [company, setCompany] = useState<any>(product?.company);
  const [commodities, setCommodities] = useState<Commodity[]>([]);
  const [commodity, setCommodity] = useState<Commodity>(Commodity.create(product?.commodity));
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [instrument, setInstrument] = useState(Instrument.create(product?.instrument));

  useEffect(() => {
    if (!companiesData?.companies) return;
    const data = companiesData.companies
      .filter(
        (c: ICompany) =>
          RoleUtility.isClient(c.role) && c.businessTypes.includes(BusinessType.Derivatives),
      )
      .map((c: ICompany) => ({ value: c.id, label: c.name }));

    setCompanies(data);
  }, [companiesData]);

  useEffect(() => {
    const data = (commoditiesData?.commodities || []).map((c: ICommodity) => Commodity.create(c));
    setCommodities(data);
  }, [commoditiesData]);

  useEffect(() => {
    const data = (getInstrumentsApi.data?.instruments || [])
      .map((i: IInstrument) => Instrument.create(i))
      .filter((i: Instrument) => !(i.isInsurance() || i.isAccumulator()));

    setInstruments(data);
  }, [getInstrumentsApi.data]);

  useEffect(() => {
    if (commodity.valid) getInstrumentsApi.request(commodity.id, companyId);
  }, [commodity, companyId]);

  useEffect(() => {
    formRenderProps.onChange("product", { value: { company, commodity, instrument } });
  }, [commodity, company, instrument]);

  const resetField = (field: string) => {
    formRenderProps.onChange(field, { value: 0 });
  };

  const onCompanyChange = (event: any) => {
    const value = event.value.value;
    setCompanyId(value);
    setCompany(UserCompany.create(companiesData.companies.find((c: ICompany) => c.id === value)));

    const clearCommodity = !commodity.selectable(value);
    if (clearCommodity) {
      setCommodity(Commodity.EMPTY);
      resetField("commodity");
    }

    if (clearCommodity || !instrument.selectable(value)) {
      setInstrument(Instrument.EMPTY);
      resetField("instrument");
    }
  };

  const onCommodityChange = (event: any) => {
    setCommodity(commodities.find((c: Commodity) => c.id == event.value.value) || Commodity.EMPTY);
    resetField("instrument");
  };

  const onInstrumentChange = (event: any) => {
    setInstrument(
      instruments.find((i: Instrument) => i.id == event.value.value) || Instrument.EMPTY,
    );

    formRenderProps.onChange("product", { value: { company, commodity, instrument } });
  };

  return (
    <div className="stepper-form" style={{ display: "flex", justifyContent: "center" }}>
      <div style={{ width: "60%" }}>
        <FormElement>
          {/* <div className="k-container k-gap-1 k-gap-sm-6">
        <div className="k-row k-col-gap-1 k-col-gap-sm-6">
          <div className="k-col"> */}
          <LoadingIndicator loading={companiesLoading} />
          {companiesError && <Error>{companiesError}</Error>}
          {!companiesLoading && !user.roles.isClient && (
            <Field
              key="company"
              id="company"
              name="company"
              label="Client"
              textField="label"
              dataItemKey="value"
              onChange={onCompanyChange}
              component={FormDropDownList}
              data={companies}
              validator={requiredValidator}
              disabled={readOnly}
            />
          )}

          <LoadingIndicator loading={commoditiesLoading} />
          {commoditiesError && <Error>{commoditiesError}</Error>}
          <Field
            key="commodity"
            id="commodity"
            name="commodity"
            label="Commodity"
            textField="label"
            dataItemKey="value"
            onChange={onCommodityChange}
            component={FormDropDownList}
            data={commodities
              .filter((c: Commodity) => c.selectable(companyId))
              .map((c: ICommodity) => ({ value: c.id, label: c.name }))
              .sort((a: ListItem, b: ListItem) => a.label.localeCompare(b.label))}
            validator={requiredValidator}
            disabled={companyId <= 0 || readOnly}
          />

          <LoadingIndicator loading={getInstrumentsApi.loading} />
          {getInstrumentsApi.error && <Error>{getInstrumentsApi.error}</Error>}
          {!getInstrumentsApi.loading && (
            <Field
              key="instrument"
              id="instrument"
              name="instrument"
              label="Product"
              textField="label"
              dataItemKey="value"
              onChange={onInstrumentChange}
              component={FormDropDownList}
              data={instruments
                .filter((i: Instrument) => i.selectable(companyId))
                .map((c: IInstrument) => ({ value: c.id, label: c.name }))
                .sort((a: ListItem, b: ListItem) => a.label.localeCompare(b.label))}
              validator={requiredValidator}
              disabled={!commodity.valid || readOnly}
            />
          )}
          {/* </div>
        </div>
      </div> */}
        </FormElement>
      </div>
    </div>
  );
};
